import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ArrowLeft, Lock, CheckCircle2, XCircle, Play, Pause, Clock, RotateCcw, Award, ShieldAlert } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REWIND = 8;

// Scoped styling for the ported prototype reading content. The prototype's
// blue primary accent is remapped to the app's rose/CRM theme; amber/green/
// red are preserved for compliance banners and do/don't lists. CSS vars are
// declared on the scope so inline var() usages in the ported HTML resolve.
const READING_CSS = `
.ccm-reading{--blue:#e11d48;--blue-l:#ffe4e6;--green:#16a34a;--green-l:#dcfce7;--green-b:#bbf7d0;--red:#dc2626;--red-l:#fee2e2;--red-b:#fecaca;--amber:#d97706;--amber-l:#fef3c7;--amber-b:#fde68a;--teal:#0d9488;--purple:#7c3aed;--t1:#0f172a;--t2:#334155;--t3:#64748b;--bg:#f8fafc;--bd:#e2e8f0;--bd2:#cbd5e1;--card:#fff;--r:8px;--rl:10px;color:var(--t1)}
.ccm-reading .hero{background:linear-gradient(135deg,#9f1239,#e11d48);border-radius:var(--rl);padding:22px 24px;margin-bottom:20px;position:relative;overflow:hidden}
.ccm-reading .hero::after{content:'';position:absolute;top:-50px;right:-50px;width:190px;height:190px;border-radius:50%;background:rgba(255,255,255,.08)}
.ccm-reading .h-e{font-size:10px;font-weight:700;color:#fecdd3;text-transform:uppercase;letter-spacing:1px;margin-bottom:8px}
.ccm-reading .h-t{font-size:21px;font-weight:700;color:#fff;letter-spacing:-.3px;margin-bottom:7px}
.ccm-reading .h-d{font-size:13px;color:rgba(255,255,255,.82);line-height:1.6;max-width:520px}
.ccm-reading .bt{font-size:16px;font-weight:700;margin:22px 0 10px;letter-spacing:-.2px;color:var(--t1)}
.ccm-reading .bx{font-size:13.5px;color:var(--t2);line-height:1.75;margin-bottom:12px}
.ccm-reading .bx strong{color:var(--t1);font-weight:650}
.ccm-reading .ins{background:var(--blue-l);border-left:3px solid var(--blue);border-radius:0 var(--r) var(--r) 0;padding:13px 15px;margin:15px 0}
.ccm-reading .ins-l{font-size:10px;font-weight:700;color:var(--blue);text-transform:uppercase;letter-spacing:.5px;margin-bottom:5px}
.ccm-reading .ins-x{font-size:13px;color:#9f1239;font-style:italic;line-height:1.6}
.ccm-reading .stg{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin:15px 0}
.ccm-reading .sc{background:var(--bg);border:1px solid var(--bd);border-radius:var(--rl);padding:14px;text-align:center}
.ccm-reading .sc-v{font-size:22px;font-weight:700;color:var(--blue);letter-spacing:-.5px}
.ccm-reading .sc-l{font-size:11px;color:var(--t3);margin-top:4px;line-height:1.35}
.ccm-reading .flw{display:flex;flex-direction:column;margin:14px 0}
.ccm-reading .stp{display:flex;gap:13px;padding:12px 0;border-bottom:1px solid var(--bd)}
.ccm-reading .stp:last-child{border-bottom:none}
.ccm-reading .stp-n{width:28px;height:28px;border-radius:50%;background:var(--blue);color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.ccm-reading .stp-t{font-size:13.5px;font-weight:650;margin-bottom:3px;color:var(--t1)}
.ccm-reading .stp-d{font-size:12.5px;color:var(--t3);line-height:1.5}
.ccm-reading .tbl{width:100%;border-collapse:collapse;margin:14px 0;font-size:12.5px;border:1px solid var(--bd);border-radius:var(--r);overflow:hidden}
.ccm-reading .tbl th{background:var(--bg);color:var(--t2);font-size:11px;font-weight:700;padding:10px 12px;text-align:left;text-transform:uppercase;letter-spacing:.4px;border-bottom:1px solid var(--bd)}
.ccm-reading .tbl td{padding:10px 12px;color:var(--t2);border-bottom:1px solid var(--bd)}
.ccm-reading .tbl tr:last-child td{border-bottom:none}
.ccm-reading .cc{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:14px 0}
.ccm-reading .ccd{background:var(--card);border:1px solid var(--bd);border-radius:var(--rl);padding:15px;position:relative;overflow:hidden}
.ccm-reading .ccd::before{content:'';position:absolute;top:0;left:0;right:0;height:3px}
.ccm-reading .ccd.p::before{background:var(--blue)}
.ccm-reading .ccd.a::before{background:var(--green)}
.ccm-reading .cc-n{font-size:18px;font-weight:700;font-variant-numeric:tabular-nums;color:var(--t1)}
.ccm-reading .cc-m{font-size:12px;font-weight:600;color:var(--t2);margin:4px 0 8px}
.ccm-reading .cc-d{font-size:12px;color:var(--t3);line-height:1.5}
.ccm-reading .cmp{background:var(--amber-l);border:1px solid var(--amber-b);border-radius:var(--rl);padding:12px 14px;margin-bottom:16px;font-size:12px;color:#92400e;line-height:1.55}
.ccm-reading .dd{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:14px 0}
.ccm-reading .dd-c{border-radius:var(--rl);padding:14px}
.ccm-reading .dd-c.y{background:var(--green-l);border:1px solid var(--green-b)}
.ccm-reading .dd-c.n{background:var(--red-l);border:1px solid var(--red-b)}
.ccm-reading .dd-t{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;margin-bottom:9px}
.ccm-reading .dd-c.y .dd-t{color:var(--green)}
.ccm-reading .dd-c.n .dd-t{color:var(--red)}
.ccm-reading .dd-i{font-size:12.5px;color:var(--t2);line-height:1.45;margin-bottom:6px;padding-left:14px;position:relative}
.ccm-reading .dd-i::before{content:'';position:absolute;left:0;top:7px;width:5px;height:5px;border-radius:50%}
.ccm-reading .dd-c.y .dd-i::before{background:var(--green)}
.ccm-reading .dd-c.n .dd-i::before{background:var(--red)}
.ccm-reading .gl{padding:12px 0;border-bottom:1px solid var(--bd);display:grid;grid-template-columns:150px 1fr;gap:15px}
.ccm-reading .gl:last-child{border-bottom:none}
.ccm-reading .gl-t{font-size:13px;font-weight:700;color:var(--blue)}
.ccm-reading .gl-d{font-size:12.5px;color:var(--t2);line-height:1.55}
@media (max-width:640px){.ccm-reading .cc,.ccm-reading .dd,.ccm-reading .stg{grid-template-columns:1fr}.ccm-reading .gl{grid-template-columns:1fr}}
`;

// ── YouTube IFrame API loader (singleton) ──────────────────────────────
let ytReadyPromise = null;
function loadYT() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytReadyPromise) return ytReadyPromise;
  ytReadyPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { prev && prev(); resolve(window.YT); };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
  });
  return ytReadyPromise;
}

const fmtTime = (s) => {
  if (s == null || isNaN(s)) return "--:--";
  const m = Math.floor(s / 60), ss = Math.floor(s % 60);
  return `${m}:${String(ss).padStart(2, "0")}`;
};

// Weighted score across all answered questions in the course.
function computeScore(chapters, answers, weights) {
  let earned = 0, possible = 0, mandatoryFailed = false;
  chapters.forEach((ch) => (ch.questions || []).forEach((q) => {
    const w = (weights && weights[q.type]) || 1;
    possible += w;
    const a = answers[q.id];
    if (a != null && a === q.correctIndex) earned += w;
    if (q.mandatory && a != null && a !== q.correctIndex) mandatoryFailed = true;
    if (q.mandatory && a == null) mandatoryFailed = true; // not yet passed
  }));
  return { pct: possible ? earned / possible : 0, mandatoryFailed };
}

export default function GatedCourseView({ course, onBack }) {
  const { token } = useAuth();
  const chapters = useMemo(() => course.chapters || [], [course]);
  const storeKey = `lp_gated_${course.id}`;

  const [state, setState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storeKey) || "{}"); } catch { return {}; }
  });
  const answers = state.answers || {};          // { questionId: chosenIndex }
  const [activeIdx, setActiveIdx] = useState(state.lastChapterIdx || 0);
  const [viewMode, setViewMode] = useState("normal"); // normal | theatre | min
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [locked, setLocked] = useState(false);     // checkpoint gate active
  const [activeQid, setActiveQid] = useState(null);
  const [feedbackQid, setFeedbackQid] = useState(null);

  const playerRef = useRef(null);
  const pollRef = useRef(null);
  const hostRef = useRef(null);

  const chapter = chapters[activeIdx] || {};
  const questions = chapter.questions || [];

  // Debounced push of the full resume snapshot to the server so
  // progress (answers + last position) carries across devices.
  const serverTimer = useRef(null);
  const pushServer = useCallback((snap) => {
    if (!token) return;
    clearTimeout(serverTimer.current);
    serverTimer.current = setTimeout(() => {
      axios.put(`${API}/learning-videos/gated-state/${course.id}`, {
        answers: snap.answers || {},
        last_chapter_idx: snap.lastChapterIdx || 0,
        last_chapter_seconds: snap.lastChapterSeconds || 0,
      }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }, 1200);
  }, [token, course.id]);

  const persist = useCallback((patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch, _updatedAt: Date.now() };
      try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch { /* ignore */ }
      pushServer(next);
      return next;
    });
  }, [storeKey, pushServer]);

  const answersRef = useRef(answers);
  useEffect(() => { answersRef.current = answers; }, [answers]);
  const setAnswer = useCallback((qid, idx) => {
    persist({ answers: { ...(answersRef.current || {}), [qid]: idx } });
  }, [persist]);

  // Cross-device hydrate: on mount pull the saved gated state. Answers
  // are unioned; resume position adopts the server copy when it's newer
  // than the local one so the learner lands where they last were.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!token || hydratedRef.current) return;
    hydratedRef.current = true;
    let cancelled = false;
    axios.get(`${API}/learning-videos/gated-state/${course.id}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (cancelled || !data) return;
        const srvUpdated = data.updated_at ? Date.parse(data.updated_at) : 0;
        setState((prev) => {
          const localUpdated = prev._updatedAt || 0;
          const useServerResume = srvUpdated >= localUpdated;
          const next = {
            ...prev,
            answers: { ...(prev.answers || {}), ...(data.answers || {}) },
            lastChapterIdx: useServerResume ? (data.last_chapter_idx ?? prev.lastChapterIdx) : prev.lastChapterIdx,
            lastChapterSeconds: useServerResume ? (data.last_chapter_seconds ?? prev.lastChapterSeconds) : prev.lastChapterSeconds,
          };
          try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch { /* ignore */ }
          if (useServerResume && data.last_chapter_idx != null) setActiveIdx(data.last_chapter_idx);
          return next;
        });
      }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, course.id]);

  // Checkpoint seconds for a question (explicit seconds, else fraction*duration).
  const cpSeconds = useCallback((q) => {
    if (q.checkpointSeconds != null) return q.checkpointSeconds;
    if (q.checkpointFraction != null && duration) return q.checkpointFraction * duration;
    return null;
  }, [duration]);

  // The next unpassed checkpoint at/under the given time. A checkpoint
  // is only satisfied once answered CORRECTLY — a wrong answer keeps the
  // gate closed so the learner must get it right to continue.
  const dueCheckpoint = useCallback((t) => {
    for (const q of questions) {
      const cs = cpSeconds(q);
      if (cs == null) continue;
      if (t >= cs - 0.4 && answersRef.current[q.id] !== q.correctIndex) return q;
    }
    return null;
  }, [questions, cpSeconds]);

  // ── Build / rebuild the YT player when the active chapter changes ──────
  useEffect(() => {
    let disposed = false;
    setLocked(false); setActiveQid(null); setFeedbackQid(null); setDuration(0); setCurrent(0); setPlaying(false);
    if (!chapter.youtubeId) return;
    loadYT().then((YT) => {
      if (disposed || !hostRef.current) return;
      if (playerRef.current) { try { playerRef.current.destroy(); } catch { /* noop */ } playerRef.current = null; }
      const div = document.createElement("div");
      hostRef.current.innerHTML = "";
      hostRef.current.appendChild(div);
      playerRef.current = new YT.Player(div, {
        videoId: chapter.youtubeId,
        playerVars: { controls: 0, modestbranding: 1, rel: 0, disablekb: 1, fs: 0, playsinline: 1, iv_load_policy: 3, start: activeIdx === (state.lastChapterIdx || 0) ? Math.floor(state.lastChapterSeconds || 0) : 0 },
        events: {
          onReady: (e) => { setDuration(e.target.getDuration() || 0); },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) { setPlaying(true); if (!duration) setDuration(e.target.getDuration() || 0); }
            else if (e.data === window.YT.PlayerState.PAUSED || e.data === window.YT.PlayerState.ENDED) setPlaying(false);
          },
        },
      });
    });
    return () => { disposed = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx, chapter.youtubeId]);

  // ── Poll playback → detect checkpoints, persist resume position ────────
  useEffect(() => {
    pollRef.current = setInterval(() => {
      const p = playerRef.current;
      if (!p || !p.getCurrentTime) return;
      const t = p.getCurrentTime() || 0;
      setCurrent(t);
      if (activeIdx === (stateRef.current.lastChapterIdx ?? activeIdx)) {
        if (Math.abs(t - (stateRef.current.lastChapterSeconds || 0)) > 3) {
          persist({ lastChapterIdx: activeIdx, lastChapterSeconds: t });
        }
      }
      if (!lockedRef.current) {
        const due = dueCheckpoint(t);
        if (due) {
          try { p.pauseVideo(); } catch { /* noop */ }
          setLocked(true); setActiveQid(due.id); setFeedbackQid(null);
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="gated-question-${due.id}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 80);
        }
      }
    }, 400);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx, dueCheckpoint, persist]);

  const lockedRef = useRef(locked); useEffect(() => { lockedRef.current = locked; }, [locked]);
  const stateRef = useRef(state); useEffect(() => { stateRef.current = state; }, [state]);

  const togglePlay = () => {
    const p = playerRef.current; if (!p || locked) return;
    try { playing ? p.pauseVideo() : p.playVideo(); } catch { /* noop */ }
  };
  const answerQuestion = (q, idx) => { setAnswer(q.id, idx); setFeedbackQid(q.id); };
  const continueVideo = () => {
    setLocked(false); setActiveQid(null); setFeedbackQid(null);
    try { playerRef.current && playerRef.current.playVideo(); } catch { /* noop */ }
  };
  const seekTo = (secs) => { try { playerRef.current.seekTo(Math.max(0, secs), true); playerRef.current.playVideo(); } catch { /* noop */ } };

  const retryChapter = () => {
    const na = { ...answersRef.current };
    questions.forEach((q) => { delete na[q.id]; });
    persist({ answers: na });
    setLocked(false); setActiveQid(null); setFeedbackQid(null);
    try { playerRef.current.seekTo(0, true); playerRef.current.pauseVideo(); } catch { /* noop */ }
  };

  // Completion helpers — a chapter is done only when every question is
  // answered correctly (force-correct gate guarantees this on watch).
  const chapterDone = (ch) => ch.isOverview || (ch.questions || []).every((q) => answers[q.id] === q.correctIndex);
  const completedCount = chapters.filter(chapterDone).length;
  const courseComplete = completedCount === chapters.length;
  const { pct, mandatoryFailed } = computeScore(chapters, answers, course.scoringWeights);
  const passThreshold = course.passThreshold || 0.7;
  const certified = courseComplete && pct >= passThreshold && !mandatoryFailed;
  const coursePct = Math.round((completedCount / chapters.length) * 100);

  // Persist chapter completion to server (best-effort) when it flips done.
  useEffect(() => {
    if (chapterDone(chapter) && token) {
      axios.post(`${API}/learning-videos/progress/${course.id}/${chapter.id}/complete`, {},
        { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, activeIdx]);

  const accent = "#E11D48"; // rose
  const gridCols = viewMode === "theatre" ? "lg:grid-cols-[220px_2.6fr_1fr]" : "lg:grid-cols-[220px_1.5fr_1fr]";
  const answeredIn = (ch) => (ch.questions || []).filter((q) => answers[q.id] != null).length;
  const heroSub = questions.length
    ? `${questions.length} knowledge check${questions.length > 1 ? "s" : ""} · ${chapter.estimated_time || ""}`
    : `Course overview · ${chapter.estimated_time || ""}`;

  return (
    <div data-testid={`gated-course-${course.id}`}>
      <style>{READING_CSS}</style>
      <button onClick={onBack} className="flex items-center gap-1.5 text-xs font-semibold hover:underline mb-4" style={{ color: accent }} data-testid="gated-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Learning Paths
      </button>

      <div className="rounded-2xl p-5 mb-4 text-white" style={{ background: "linear-gradient(135deg,#9F1239,#E11D48)" }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-bold text-white">{course.title}</h2>
            <p className="text-[12px] text-white/80 mt-0.5 max-w-xl">{course.description}</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold" data-testid="gated-progress-pct">{coursePct}%</div>
            <div className="text-[11px] text-white/70">{completedCount} of {chapters.length} chapters · score {Math.round(pct * 100)}%</div>
          </div>
        </div>
        {certified && (
          <div className="mt-3 flex items-center gap-2 rounded-xl bg-white/15 px-3 py-2" data-testid="gated-certificate">
            <Award className="h-5 w-5" />
            <span className="text-[13px] font-semibold">Certified — {course.title}. Passed {Math.round(pct * 100)}% on {new Date().toLocaleDateString()}.</span>
          </div>
        )}
      </div>

      <div className={`grid gap-4 grid-cols-1 ${gridCols}`}>
        {/* Chapter list */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="gated-chapter-list">
          <div className="px-3 py-3 border-b border-slate-100">
            <div className="text-[13px] font-bold text-slate-800">{course.title}</div>
            <div className="text-[11px] text-slate-500 mt-1" data-testid="gated-chapter-progress">{completedCount} of {chapters.length} chapters · {coursePct}%</div>
            <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${coursePct}%`, background: accent }} />
            </div>
          </div>
          <div className="p-2 max-h-[520px] overflow-auto">
            {chapters.map((ch, i) => {
              const done = chapterDone(ch);
              const total = (ch.questions || []).length;
              const ans = answeredIn(ch);
              const full = total > 0 && ans === total;
              return (
                <button key={ch.id} data-testid={`gated-chapter-${ch.id}`}
                  onClick={() => { setActiveIdx(i); persist({ lastChapterIdx: i, lastChapterSeconds: 0 }); }}
                  className={`w-full text-left px-2.5 py-2 rounded-lg mb-1 flex items-start gap-2.5 transition-colors ${i === activeIdx ? "bg-rose-50" : "hover:bg-slate-50"}`}>
                  <span className="flex-shrink-0 mt-0.5">
                    {done
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      : <span className={`inline-block h-4 w-4 rounded-full border ${i === activeIdx ? "border-rose-400" : "border-slate-300"}`} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block text-[10px] font-bold tracking-wide ${i === activeIdx ? "text-rose-600" : "text-slate-400"}`}>CH {i + 1}</span>
                    <span className={`block text-[12.5px] leading-snug ${i === activeIdx ? "font-semibold text-rose-700" : "text-slate-700"}`}>{ch.title}</span>
                    <span className="mt-1 flex items-center gap-1.5 text-[10.5px] text-slate-400">
                      <Clock className="h-3 w-3" /> {ch.estimated_time}
                      <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[9.5px] font-bold ${total === 0 ? "bg-slate-100 text-slate-500" : full ? "bg-emerald-100 text-emerald-700" : ans ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`} data-testid={`gated-chapter-badge-${ch.id}`}>
                        {total === 0 ? "Overview" : `${ans}/${total}`}
                      </span>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Video + reading */}
        <div>
          {/* Minimized slim bar */}
          <div className={`${viewMode === "min" ? "flex" : "hidden"} items-center gap-3 px-4 h-14 rounded-xl bg-slate-900 text-white`} data-testid="gated-minbar">
            <Play className="h-3.5 w-3.5 opacity-70" />
            <span className="flex-1 text-[13px] font-semibold truncate">CH {activeIdx + 1} · {chapter.title}</span>
            <button onClick={() => setViewMode("normal")} className="text-[12px] font-semibold text-rose-300 hover:text-rose-200" data-testid="gated-expand">Expand video ↑</button>
          </div>

          {/* Full player (kept mounted; only visually collapsed when minimized) */}
          <div className={`relative rounded-xl overflow-hidden bg-black ${viewMode === "min" ? "hidden" : ""}`} data-testid="gated-video-wrap">
            <div className="relative" style={{ paddingBottom: "56.25%" }}>
              <div ref={hostRef} className="absolute inset-0 w-full h-full" data-testid="gated-video" />
              {locked && (
                <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center text-white z-10" data-testid="gated-lock-overlay">
                  <Lock className="h-8 w-8 mb-2" />
                  <div className="text-sm font-semibold">Answer the checkpoint question to continue</div>
                </div>
              )}
              {/* View-mode controls (top-right overlay) */}
              <div className="absolute top-2.5 right-2.5 flex gap-1.5 z-20">
                {[["normal", "Default"], ["theatre", "Theatre"], ["min", "Minimize"]].map(([m, label]) => (
                  <button key={m} data-testid={`gated-view-${m}`} onClick={() => setViewMode(m)}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-md backdrop-blur transition-colors ${viewMode === m ? "bg-rose-600 text-white" : "bg-black/45 text-white/85 hover:bg-black/65"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {/* Custom scrubber + controls */}
            <div className="bg-slate-900 px-3 py-2.5">
              <div className="flex items-center gap-3">
                <button onClick={togglePlay} data-testid="gated-playpause" className="text-white/85 hover:text-white flex-shrink-0">
                  {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </button>
                <div className="relative flex-1 h-4 flex items-center cursor-pointer" data-testid="gated-scrubber"
                  onClick={(e) => {
                    if (!duration) return;
                    const r = e.currentTarget.getBoundingClientRect();
                    const p = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
                    seekTo(p * duration);
                  }}>
                  <div className="w-full h-1 rounded-full bg-white/25">
                    <div className="h-full rounded-full bg-rose-500" style={{ width: duration ? `${(current / duration) * 100}%` : "0%" }} />
                  </div>
                  {questions.map((q) => {
                    const cs = cpSeconds(q); if (cs == null || !duration) return null;
                    const passed = answers[q.id] === q.correctIndex;
                    const at = locked && activeQid === q.id;
                    return (
                      <button key={q.id} data-testid={`gated-marker-${q.id}`} title={`Checkpoint · ${fmtTime(cs)}`}
                        onClick={(e) => { e.stopPropagation(); seekTo(cs - REWIND); }}
                        className="absolute h-2.5 w-2.5 rounded-[2px] border border-black/40"
                        style={{ left: `${(cs / duration) * 100}%`, top: "50%", transform: "translate(-50%,-50%) rotate(45deg)", background: passed ? "#4ade80" : at ? "#fff" : "#fbbf24", boxShadow: at ? "0 0 0 4px rgba(251,191,36,.45)" : "none" }} />
                    );
                  })}
                </div>
                <span className="text-[11.5px] text-white/85 tabular-nums whitespace-nowrap">{fmtTime(current)} / {fmtTime(duration)}</span>
                <button data-testid="gated-retry" onClick={retryChapter} className="text-white/80 hover:text-white flex items-center gap-1 text-[11px] flex-shrink-0" title="Restart chapter">
                  <RotateCcw className="h-3.5 w-3.5" /> Retry
                </button>
              </div>
            </div>
          </div>

          {/* Reading content */}
          <div className="mt-3 rounded-xl border border-slate-200 bg-white p-5 ccm-reading" data-testid="gated-reading">
            <div className="hero">
              <div className="h-e">Chapter {activeIdx + 1} of {chapters.length}</div>
              <div className="h-t">{chapter.title}</div>
              <div className="h-d">{heroSub}</div>
            </div>
            {chapter.readingHtml
              ? <div dangerouslySetInnerHTML={{ __html: chapter.readingHtml }} />
              : (chapter.sections || []).map((s, i) => (
                  <div key={i} className="text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed">{s.content}</div>
                ))}
          </div>
        </div>

        {/* Question panel */}
        <div className="rounded-xl border border-slate-200 bg-white p-3 max-h-[620px] overflow-auto" data-testid="gated-question-panel">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-500 mb-1">Knowledge checks</div>
          <div className="text-[12px] text-slate-400 mb-2" data-testid="gated-kc-sub">
            {questions.length === 0
              ? "Overview chapter — no questions. Watch to complete."
              : `${answeredIn(chapter)} of ${questions.length} answered`}
          </div>
          {questions.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-[12px] text-slate-500" data-testid="gated-overview-note">
              This chapter is an overview — no knowledge checks. Watch through, then continue to the next chapter.
            </div>
          )}
          {questions.map((q) => {
            const cs = cpSeconds(q);
            const attempted = answers[q.id] != null;
            const passed = answers[q.id] === q.correctIndex;
            const wrong = attempted && !passed;
            const reached = activeQid === q.id || attempted || (cs != null && current >= cs - 0.4);
            return (
              <div key={q.id} data-testid={`gated-question-${q.id}`} data-answered={passed ? "true" : "false"}
                className={`mb-3 rounded-lg border p-2.5 transition-all ${!reached ? "bg-slate-50 border-slate-200 opacity-70" : passed ? "border-emerald-300" : wrong ? "border-rose-300" : activeQid === q.id ? "border-rose-400 ring-2 ring-rose-100" : "border-slate-300"}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    {q.type === "tf" ? "True / False" : q.type}
                    {q.mandatory && <span className="ml-1 text-rose-600" data-testid="gated-must-pass">· Must pass</span>}
                  </span>
                  {cs != null && <span className="text-[10px] text-slate-400 flex items-center gap-1">{!reached && <Lock className="h-3 w-3" />}{fmtTime(cs)}</span>}
                </div>
                {!reached ? (
                  <div className="text-[12px] text-slate-400 italic flex items-center gap-1.5"><Lock className="h-3 w-3" /> Locked — reach {fmtTime(cs)} in the video.</div>
                ) : (
                  <>
                    {q.mandatory && <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold text-rose-600"><ShieldAlert className="h-3 w-3" /> Required for certification</div>}
                    <div className="text-[12.5px] font-medium text-slate-800 mb-2">{q.question}</div>
                    <div className="space-y-1.5">
                      {q.options.map((opt, oi) => {
                        const chosen = answers[q.id] === oi;
                        const showRight = passed && oi === q.correctIndex;
                        const showWrong = chosen && oi !== q.correctIndex;
                        return (
                          <button key={oi} data-testid={`gated-opt-${q.id}-${oi}`} disabled={passed}
                            onClick={() => answerQuestion(q, oi)}
                            className={`w-full text-left text-[12px] px-2.5 py-1.5 rounded-md border flex items-start gap-2 ${showRight ? "border-emerald-400 bg-emerald-50 text-emerald-700" : showWrong ? "border-rose-400 bg-rose-50 text-rose-700" : chosen ? "border-rose-300" : "border-slate-200 hover:border-rose-300"} ${passed ? "cursor-default" : ""}`}>
                            <span className="flex-shrink-0 mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded border border-current text-[9px] font-bold opacity-70">{"ABCD"[oi]}</span>
                            <span>{opt}</span>
                          </button>
                        );
                      })}
                    </div>
                    {feedbackQid === q.id && passed && (
                      <div className="mt-2 rounded-md p-2 text-[11.5px] bg-emerald-50 text-emerald-800" data-testid={`gated-feedback-${q.id}`}>
                        <div className="flex items-center gap-1 font-semibold mb-0.5"><CheckCircle2 className="h-3.5 w-3.5" /> Correct</div>
                        {q.feedback}
                        {locked && activeQid === q.id && (
                          <button data-testid={`gated-continue-${q.id}`} onClick={continueVideo} className="mt-2 block w-full text-center text-[12px] font-semibold text-white rounded-md py-1.5" style={{ background: accent }}>Continue video →</button>
                        )}
                      </div>
                    )}
                    {feedbackQid === q.id && wrong && (
                      <div className="mt-2 rounded-md p-2 text-[11.5px] bg-rose-50 text-rose-800" data-testid={`gated-feedback-${q.id}`}>
                        <div className="flex items-center gap-1 font-semibold"><XCircle className="h-3.5 w-3.5" /> Not quite — try again</div>
                        <div className="mt-0.5 text-rose-700/90">Pick another answer to continue.</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
